/**
 * A realm's calendars and the arithmetic over them (docs/PARITY-STEP-3-PLAN.md section 3),
 * ported from the desktop's `domain.CalendarSystem` and `service.CalendarEngine` exactly
 * (decision 5): everything is a function of an "absolute day", the same number a
 * chronology date's sort key carries; years repeat identically, with no leap years; a
 * negative day counts back through the years before the epoch. Checked against
 * schema/fixtures/calendar-cases.json, the desktop's own answers.
 *
 * Days are numbers, exact up to 2^53 - 1 (decision 2). Where the desktop divides a `long`
 * and casts to `int`, this does the same in `BigInt`, so a year far enough out wraps as
 * Java's does.
 */

export interface CalendarMonth {
  readonly name: string;
  readonly lengthInDays: number;
}

/** A span of the year; months are 0-based and days 1-based. A start after its end wraps past the year's end. */
export interface Season {
  readonly name: string;
  readonly startMonth: number;
  readonly startDay: number;
  readonly endMonth: number;
  readonly endDay: number;
}

/** A cycle in days, which may be fractional, and the absolute day it was new on. */
export interface Moon {
  readonly name: string;
  readonly cycleLengthDays: number;
  readonly phaseOffsetDays: number;
}

/** An occasion on a month and day: every year when `year` is null, otherwise that year only. */
export interface Festival {
  readonly name: string;
  readonly description: string;
  readonly monthIndex: number;
  readonly day: number;
  readonly year: number | null;
}

export interface CalendarSystem {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly months: readonly CalendarMonth[];
  readonly daysPerWeek: number;
  readonly weekdayNames: readonly string[];
  readonly hoursPerDay: number;
  /** The year absolute day 0 falls in. */
  readonly epochYear: number;
  readonly seasons: readonly Season[];
  readonly moons: readonly Moon[];
  readonly festivals: readonly Festival[];
  readonly created: string;
  readonly modified: string;
}

/** One absolute day in a calendar; `monthIndex` is 0-based. */
export interface CalendarDate {
  readonly year: number;
  readonly monthIndex: number;
  readonly day: number;
}

/** A calendar the desktop would refuse to load: a month with no days, or a moon with no cycle. */
export class CalendarError extends Error {}

export function daysPerYear(calendar: CalendarSystem): number {
  return calendar.months.reduce((total, month) => total + month.lengthInDays, 0);
}

export function toAbsoluteDay(calendar: CalendarSystem, year: number, monthIndex: number, day: number): number {
  const yearsFromEpoch = BigInt(year) - BigInt(calendar.epochYear);
  const dayOfYear = dayOfYearStart(calendar, monthIndex) + (day - 1);
  return Number(BigInt.asIntN(64, yearsFromEpoch * BigInt(daysPerYear(calendar)) + BigInt(dayOfYear)));
}

export function fromAbsoluteDay(calendar: CalendarSystem, absoluteDay: number): CalendarDate {
  const perYear = daysPerYear(calendar);
  const yearsFromEpoch = floorDiv(absoluteDay, perYear);
  const dayOfYear = floorMod(absoluteDay, perYear);
  // Java's `(int) (epochYear + yearsFromEpoch)`.
  const year = Number(BigInt.asIntN(32, BigInt(calendar.epochYear) + yearsFromEpoch));
  let remaining = dayOfYear;
  for (let i = 0; i < calendar.months.length; i++) {
    const length = calendar.months[i]!.lengthInDays;
    if (remaining < length) return { year, monthIndex: i, day: remaining + 1 };
    remaining -= length;
  }
  const last = calendar.months.length - 1;
  return { year, monthIndex: last, day: calendar.months[last]!.lengthInDays };
}

export function weekdayName(calendar: CalendarSystem, absoluteDay: number): string {
  const index = floorMod(absoluteDay, calendar.daysPerWeek);
  return index < calendar.weekdayNames.length ? calendar.weekdayNames[index]! : `Day ${index + 1}`;
}

/** The first season, in the calendar's order, that the day falls in. */
export function seasonAt(calendar: CalendarSystem, absoluteDay: number): Season | null {
  const dayOfYear = floorMod(absoluteDay, daysPerYear(calendar));
  for (const season of calendar.seasons) {
    const start = dayOfYearStart(calendar, season.startMonth) + (season.startDay - 1);
    const end = dayOfYearStart(calendar, season.endMonth) + (season.endDay - 1);
    const inSeason = start <= end ? dayOfYear >= start && dayOfYear <= end : dayOfYear >= start || dayOfYear <= end;
    if (inSeason) return season;
  }
  return null;
}

/** 0 is new, 0.25 the first quarter, 0.5 full, 0.75 the last quarter, back to new at 1. */
export function moonPhaseFraction(moon: Moon, absoluteDay: number): number {
  // The same floating-point steps as Java's double `%`, which JavaScript's matches.
  let t = (absoluteDay - moon.phaseOffsetDays) % moon.cycleLengthDays;
  if (t < 0) t += moon.cycleLengthDays;
  return t / moon.cycleLengthDays;
}

export const PHASE_NAMES = [
  'New Moon',
  'Waxing Crescent',
  'First Quarter',
  'Waxing Gibbous',
  'Full Moon',
  'Waning Gibbous',
  'Last Quarter',
  'Waning Crescent',
] as const;

export function moonPhaseName(fraction: number): string {
  let index = javaIntCast(Math.floor(fraction * PHASE_NAMES.length)) % PHASE_NAMES.length;
  if (index < 0) index += PHASE_NAMES.length;
  // `| 0` turns a -0 into the 0 it indexes as in Java.
  return PHASE_NAMES[index | 0]!;
}

/** Every festival on the day: the recurring ones, and any pinned to its year. */
export function festivalsOn(calendar: CalendarSystem, absoluteDay: number): Festival[] {
  const date = fromAbsoluteDay(calendar, absoluteDay);
  return calendar.festivals.filter((f) => f.monthIndex === date.monthIndex && f.day === date.day && (f.year === null || f.year === date.year));
}

/** "Year 17, Frostmoon 3", as the desktop's Date Lookup writes a date. */
export function formatCalendarDate(calendar: CalendarSystem, absoluteDay: number): string {
  const date = fromAbsoluteDay(calendar, absoluteDay);
  return `Year ${date.year}, ${calendar.months[date.monthIndex]!.name} ${date.day}`;
}

function dayOfYearStart(calendar: CalendarSystem, monthIndex: number): number {
  let total = 0;
  for (let i = 0; i < monthIndex && i < calendar.months.length; i++) total += calendar.months[i]!.lengthInDays;
  return total;
}

/** `Math.floorDiv(long, int)`, exactly, as a `BigInt`. */
function floorDiv(a: number, b: number): bigint {
  const x = BigInt(a);
  const y = BigInt(b);
  const q = x / y;
  return (x % y !== 0n) && ((x < 0n) !== (y < 0n)) ? q - 1n : q;
}

/** `Math.floorMod(long, int)`: always between 0 and `b - 1` for a positive `b`. */
function floorMod(a: number, b: number): number {
  const m = BigInt(a) % BigInt(b);
  return Number(m !== 0n && (m < 0n) !== (b < 0) ? m + BigInt(b) : m);
}

/** Java's `(int)` of a double: NaN is 0, and out-of-range values stop at the int limits. */
function javaIntCast(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.max(-2147483648, Math.min(2147483647, Math.trunc(value)));
}
